import { allArticles } from "./docs";
import { getFaqList } from "./faq";
import type { Lang } from "../i18n";

export interface SearchHit {
  type: "doc" | "faq";
  /** Route to open: doc → /docs/<slug>, faq → /faq/<id>. */
  route: string;
  title: string;
  /** Breadcrumb context, e.g. "Operator Guide · Monitoring" or "FAQ". */
  context: string;
  snippet: string;
}

interface Indexed {
  type: "doc" | "faq";
  route: string;
  title: string;
  context: string;
  haystackTitle: string;
  haystack: string;
  body: string; // source for snippets
}

// Strip Markdown to plain text for search/snippets.
function plain(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// "FAQ" breadcrumb per language.
const FAQ_CONTEXT: Record<Lang, string> = { en: "FAQ", fr: "FAQ" };

function buildIndex(lang: Lang): Indexed[] {
  return [
    ...allArticles(lang).map((a) => {
      const text = plain(a.body);
      return {
        type: "doc" as const,
        route: `/docs/${a.slug}`,
        title: a.title,
        context: a.sectionTitle
          ? `${a.guideTitle} · ${a.sectionTitle}`
          : a.guideTitle,
        haystackTitle: a.title.toLowerCase(),
        haystack: `${a.title} ${a.who} ${a.where} ${text}`.toLowerCase(),
        body: text,
      };
    }),
    ...getFaqList(lang).map((f) => ({
      type: "faq" as const,
      route: `/faq/${f.id}`,
      title: f.question,
      context: FAQ_CONTEXT[lang],
      haystackTitle: f.question.toLowerCase(),
      haystack: `${f.question} ${f.answer} ${f.keywords}`.toLowerCase(),
      body: plain(f.answer),
    })),
  ];
}

// Build each language's index once, lazily.
const indexes: Partial<Record<Lang, Indexed[]>> = {};
function getIndex(lang: Lang): Indexed[] {
  return (indexes[lang] ??= buildIndex(lang));
}

function snippet(body: string, term: string): string {
  const i = body.toLowerCase().indexOf(term);
  if (i < 0) return body.length > 160 ? body.slice(0, 157).trimEnd() + "…" : body;
  const start = Math.max(0, i - 70);
  const end = Math.min(body.length, i + term.length + 90);
  return (start > 0 ? "… " : "") + body.slice(start, end).trim() + (end < body.length ? " …" : "");
}

export function searchContent(lang: Lang, query: string, limit = 25): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(Boolean);

  const scored: { item: Indexed; score: number }[] = [];
  for (const item of getIndex(lang)) {
    // Require every term to appear somewhere (AND match).
    if (!terms.every((t) => item.haystack.includes(t))) continue;

    let score = 0;
    for (const t of terms) {
      if (item.haystackTitle.includes(t)) score += 4;
      if (item.haystack.includes(t)) score += 1;
    }
    if (item.haystackTitle.includes(q)) score += 6; // exact phrase in title
    if (item.type === "faq") score += 1; // nudge FAQs up for quick answers
    scored.push({ item, score });
  }

  scored.sort((a, b) => b.score - a.score);
  const firstTerm = terms[0];
  return scored.slice(0, limit).map(({ item }) => ({
    type: item.type,
    route: item.route,
    title: item.title,
    context: item.context,
    snippet: snippet(item.body, firstTerm),
  }));
}
